import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navigation from "./Navigation";
import TrialStatus from "./TrialStatus";
import ReviewStatus from "./ReviewStatus";
import FeedbackButton from "./FeedbackButton";
import { useEnvironment } from "../contexts/EnvironmentContext";
import logoTransparentv2 from "../assets/logoTransparentv2.png";

const ACCENT = "#9CCCD4";

// Custom hook for easy localStorage-backed state
function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore write errors
    }
  }, [key, value]);

  return [value, setValue];
}

const TABS = [
  { key: "full", label: "📦 Full Review" },
  { key: "modified", label: "📝 Modified Files Only" },
  { key: "pr", label: "🚀 PR Suggestions" },
  { key: "inline", label: "🧬 Inline PR Annotations" },
  { key: "github", label: "🔗 GitHub PR Review" }
];

export default function CleanCodeReview({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiBaseUrl } = useEnvironment();
  
  // Payment status handling
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // Review status handling
  const [reviewStatus, setReviewStatus] = useState(null);
  
  // Fetch review status
  const fetchReviewStatus = async () => {
    try {
      // Get or create a persistent user identifier
      let userId = localStorage.getItem('userId');
      if (!userId) {
        userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('userId', userId);
      }
      
      console.log('CleanCodeReview: Fetching status for userId:', userId);
      
      const response = await fetch(`${apiBaseUrl}/api/review-status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-User-ID': userId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch review status');
      }

      const data = await response.json();
      setReviewStatus(data);
    } catch (error) {
      console.error('Error fetching review status:', error);
      // Set default status for error cases
      setReviewStatus({
        allowed: true,
        current_count: 0,
        limit: 5,
        remaining: 5,
        percentage: 0,
        plan_name: 'trial'
      });
    }
  };
  
  useEffect(() => {
    // Fetch review status on component mount
    fetchReviewStatus();
    
    const urlParams = new URLSearchParams(location.search);
    const payment = urlParams.get('payment');
    const autoLogin = urlParams.get('auto_login');
    
    if (payment === 'success') {
      setPaymentStatus('success');
      
      // If auto_login is true, ensure user is logged in
      if (autoLogin === 'true') {
        const token = localStorage.getItem('token');
        if (!token) {
          // Auto-login the user after successful checkout
          // For now, we'll create a temporary token to simulate successful login
          // In a real implementation, you'd get the user's email from Stripe session
          // and create a proper JWT token
          const tempToken = 'temp_token_' + Date.now();
          localStorage.setItem('token', tempToken);
          localStorage.setItem('userEmail', 'user@example.com'); // Temporary email
          console.log('Auto-login completed with temporary token');
          
          // Trigger a page reload to update the authentication state
          window.location.reload();
        }
      }
      
      // Clear the URL parameter after showing the message
      setTimeout(() => {
        // Only navigate if we're not already on the review page
        if (location.pathname !== '/review') {
          navigate('/review', { replace: true });
        }
      }, 5000);
      // Auto-dismiss the toast after 6 seconds
      setTimeout(() => {
        setPaymentStatus(null);
      }, 6000);
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
      // Clear the URL parameter after showing the message
      setTimeout(() => {
        // Only navigate if we're not already on the review page
        if (location.pathname !== '/review') {
          navigate('/review', { replace: true });
        }
      }, 3000);
      // Auto-dismiss the toast after 4 seconds
      setTimeout(() => {
        setPaymentStatus(null);
      }, 4000);
    }
  }, [location.search, navigate]);
  
  // --- Captcha state ---
  const [authenticated, setAuthenticated] = useState(
    () => localStorage.getItem("solvedCaptcha") === "true"
  );
  const [a, setA] = useState(0);
  const [b, setB] = useState(0);
  const [c, setC] = useState(0);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  // --- OAuth state (persisted) ---
  const [githubConnected, setGithubConnected] = useLocalStorage(
    "githubConnected",
    false
  );
  const [authMethod, setAuthMethod] = useState("OAuth");

  // --- Main UI state (initialize on GitHub tab if already connected) ---
  const [activeTab, setActiveTab] = useState(() =>
    githubConnected ? "github" : "full"
  );
  const [loaded, setLoaded] = useState(true);
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [projectId, setProjectId] = useState("");
  const [mrId, setMrId] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  // --- Generate captcha on mount ---
  useEffect(() => resetCaptcha(), []);
  const resetCaptcha = () => {
    setA(Math.floor(Math.random() * 10) + 1);
    setB(Math.floor(Math.random() * 10) + 1);
    setC(Math.floor(Math.random() * 5) + 1);
    setCaptchaInput("");
    setCaptchaError("");
  };
  const verifyCaptcha = () => {
    if (parseInt(captchaInput, 10) === (a + b) * c) {
      setAuthenticated(true);
      localStorage.setItem("solvedCaptcha", "true");
    } else {
      setCaptchaError(`Wrong: ${captchaInput}. Try (${a}+${b})×${c}.`);
      resetCaptcha();
    }
  };

  // --- Load persisted auth ---
  useEffect(() => {
    const storedConnected =
      window.localStorage.getItem("githubConnected") === "true";
    if (storedConnected) {
      setGithubConnected(true);
    }
  }, []);

  // --- Handle OAuth callback from GitHub, tab parameter from dashboard, and reviewId from history ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const githubParam = params.get("github");
    const tabParam = params.get("tab");
    const reviewIdParam = params.get("reviewId");
    
    if (githubParam === "connected") {
      console.log("GitHub OAuth successful in CleanCodeReview");
      // GitHub OAuth was successful, mark as connected
      setGithubConnected(true);
      setActiveTab("github");
      
      // Clear the URL parameter
      window.history.replaceState({}, "", window.location.pathname);
      
      // Note: In a real implementation, you'd get the GitHub token from the backend
      // For now, we'll just mark as connected and let the backend handle token storage
    } else if (tabParam) {
      // Handle tab parameter - map old tabs to new structure
      if (tabParam === "github") {
        setActiveTab("github");
      } else if (["full", "modified", "pr", "inline"].includes(tabParam)) {
        setActiveTab(tabParam);
      }
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (reviewIdParam) {
      // Handle reviewId parameter from review history
      console.log("Loading review details for ID:", reviewIdParam);
      loadReviewDetails(reviewIdParam);
      setActiveTab("full"); // Default to full tab
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // --- Sync tab when connected changes ---
  // Note: Removed this useEffect as it was causing navigation issues
  // The initial state already handles setting the tab based on githubConnected

  // --- Animate tab switching ---
  useEffect(() => {
    setLoaded(false);
    const timer = setTimeout(() => setLoaded(true), 50);
    return () => clearTimeout(timer);
  }, [activeTab]);

  // --- Handlers ---
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    e.target.value = null; // allow re-upload same file
  };

  const loadReviewDetails = async (reviewId) => {
    setLoading(true);
    setOutput("🔄 Loading review details...");
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/review-history/${reviewId}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch review details');
      }

      const data = await response.json();
      const review = data.review;
      
      // Format the review data for display
      let formattedOutput = `# 📋 Review Details: ${review.title}\n\n`;
      formattedOutput += `**Type:** ${review.type.toUpperCase()} | **Status:** ${review.status} | **Date:** ${new Date(review.createdAt).toLocaleDateString()}\n\n`;
      
      if (review.url) {
        formattedOutput += `**GitHub PR:** [${review.url}](${review.url})\n\n`;
      }
      
      formattedOutput += `## 📊 Summary\n${review.summary}\n\n`;
      
      if (review.details) {
        const details = review.details;
        formattedOutput += `## 📈 Metrics\n`;
        formattedOutput += `- **Files Analyzed:** ${details.filesAnalyzed}\n`;
        formattedOutput += `- **Lines of Code:** ${details.linesOfCode}\n`;
        formattedOutput += `- **Issues Found:** ${details.issuesFound}\n\n`;
        
        formattedOutput += `## 🎯 Code Quality Scores\n`;
        formattedOutput += `- **Complexity:** ${details.metrics.complexity}\n`;
        formattedOutput += `- **Maintainability:** ${details.metrics.maintainability}\n`;
        formattedOutput += `- **Test Coverage:** ${details.metrics.testCoverage}\n`;
        formattedOutput += `- **Performance Score:** ${details.metrics.performanceScore}\n\n`;
        
        if (details.suggestions && details.suggestions.length > 0) {
          formattedOutput += `## 🔍 Code Suggestions\n\n`;
          
          details.suggestions.forEach((suggestion, index) => {
            const severityEmoji = suggestion.severity === 'high' ? '🔴' : 
                                suggestion.severity === 'medium' ? '🟡' : '🟢';
            const typeEmoji = suggestion.type === 'security' ? '🔒' :
                             suggestion.type === 'performance' ? '⚡' :
                             suggestion.type === 'architecture' ? '🏗️' : '📝';
            
            formattedOutput += `### ${index + 1}. ${typeEmoji} ${suggestion.type.charAt(0).toUpperCase() + suggestion.type.slice(1)} Issue\n`;
            formattedOutput += `${severityEmoji} **Severity:** ${suggestion.severity}\n`;
            formattedOutput += `📁 **File:** \`${suggestion.file}\` (Line ${suggestion.line})\n`;
            formattedOutput += `💬 **Message:** ${suggestion.message}\n\n`;
            
            if (suggestion.code) {
              formattedOutput += `\`\`\`swift\n${suggestion.code}\n\`\`\`\n\n`;
            }
          });
        }
      }
      
      formattedOutput += `---\n*Review loaded from CleanCode.ai history*`;
      
      setOutput(formattedOutput);
    } catch (error) {
      console.error('Error loading review details:', error);
      setOutput(`❌ Error loading review details: ${error.message}\n\nPlease try again or contact support if the issue persists.`);
    } finally {
      setLoading(false);
    }
  };

  // --- Reset auth ---
  const handleDisconnect = () => {
    setGithubConnected(false);
    setUrl("");
    setOutput("");
    localStorage.removeItem("githubConnected");
    setActiveTab("github");
  };

  const handleDownload = (format = 'markdown') => {
    if (format === 'pdf') {
      // For PDF, we need to send the current output to the backend
      // This is a simplified approach - in production you might want to store the review first
      alert('PDF download is available for saved reviews. Please save your review first or use the Review History page.');
      return;
    }
    
    const blob = new Blob([output], { type: "text/markdown" });
    const urlBlob = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = urlBlob;
    link.download = "review.md";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  // Handle review execution
  const handleRun = async () => {
    // Check if user can perform another review
    if (reviewStatus && !reviewStatus.allowed && reviewStatus.limit !== 'unlimited') {
      alert('Monthly review limit reached. Please upgrade your plan to continue.');
      return;
    }

    setLoading(true);
    setOutput('');

    try {
      // Get or create a persistent user identifier
      let userId = localStorage.getItem('userId');
      if (!userId) {
        userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('userId', userId);
      }

      console.log('CleanCodeReview: Running review for userId:', userId);

      let response;
      const formData = new FormData();

      // Common headers for all requests
      const headers = {
        'X-User-ID': userId,
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };

      if (activeTab === 'github') {
        formData.append('pr_url', url);
        formData.append('auth_method', 'token');
        formData.append('token', '');
        
        response = await fetch(`${apiBaseUrl}/api/github_pr_review`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: headers,
        });
      } else if (activeTab === 'gitlab') {
        formData.append('mr_url', url);
        formData.append('auth_method', 'token');
        formData.append('token', '');
        formData.append('project_id', projectId);
        formData.append('mr_id', mrId);
        
        response = await fetch(`${apiBaseUrl}/api/gitlab_mr_review`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: headers,
        });
      } else if (['full', 'modified', 'pr'].includes(activeTab)) {
        if (!file) {
          alert('Please select a file first.');
          return;
        }
        
        formData.append('file', file);
        formData.append('mode', activeTab === 'full' ? 'All Files' : 
                       activeTab === 'modified' ? 'Modified Files Only' : 
                       'Pull Request Suggestions Only');
        
        response = await fetch(`${apiBaseUrl}/api/review_zip`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: headers,
        });
      } else if (activeTab === 'inline') {
        if (!file) {
          alert('Please select a file first.');
          return;
        }
        
        formData.append('file', file);
        
        response = await fetch(`${apiBaseUrl}/api/inline_annotator`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: headers,
        });
      }

      if (!response.ok) {
        if (response.status === 429) {
          alert('Monthly review limit reached. Please upgrade your plan to continue.');
          fetchReviewStatus(); // Refresh status
          return;
        }
        throw new Error('Failed to process review');
      }

      const data = await response.json();
      setOutput(data.result);
      
      // Refresh review status after successful review
      fetchReviewStatus();
    } catch (error) {
      console.error('Error:', error);
      setOutput('Error processing review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReview = async () => {
    if (!output) {
      alert('No review to save. Please run a review first.');
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/save-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify({
          title: `${TABS.find((t) => t.key === activeTab)?.label} - ${new Date().toLocaleDateString()}`,
          content: output,
          type: activeTab,
          url: url || '',
          metadata: {
            authMethod: authMethod,
            projectId: projectId || '',
            mrId: mrId || ''
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save review');
      }

      const result = await response.json();
      alert(`Review saved successfully! Review ID: ${result.review_id}`);
      
      // Refresh review status after saving
      fetchReviewStatus();
      
      // Optionally clear the output after saving
      // setOutput('');
    } catch (error) {
      console.error('Error saving review:', error);
      alert('Failed to save review. Please try again.');
    }
  };

  // --- Captcha gate ---
  if (!authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: 'linear-gradient(135deg, #232c36 0%, #3a4652 100%)' }}>
        <div className="bg-gray-800 p-8 rounded shadow-lg text-white w-full max-w-md">
          <h2 className="text-2xl mb-4">Security Check</h2>
          <p className="mb-4">
            What is (
            <strong>
              {a} + {b}
            </strong>
            ) × <strong>{c}</strong>?
          </p>
          <input
            type="text"
            value={captchaInput}
            onChange={(e) => setCaptchaInput(e.target.value)}
            placeholder="Your answer"
            className="w-full p-3 rounded mb-2 text-black"
          />
          {captchaError && <p className="text-red-400 mb-2">{captchaError}</p>}
          <button
            onClick={verifyCaptcha}
            className="w-full py-2 bg-blue-600 rounded hover:bg-blue-700 transition"
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  // --- Main UI ---
  return (
    <div>
      <Navigation onLogout={onLogout} />
      
      {/* Payment Status Notification */}
      {paymentStatus === 'success' && (
        <div className="bg-green-600 text-white p-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold mb-2">🎉 Payment Successful!</h2>
            <p>Your subscription is now active. Welcome to CleanCode.ai!</p>
          </div>
        </div>
      )}
      
      {paymentStatus === 'cancelled' && (
        <div className="bg-yellow-600 text-white p-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold mb-2">Payment Cancelled</h2>
            <p>You can try again anytime. Your trial period is still active.</p>
          </div>
        </div>
      )}
      
      {/* Review Status Component */}
      <div className="max-w-4xl mx-auto p-6">
        <ReviewStatus reviewStatus={reviewStatus} />
      </div>
      
      <div className="relative max-w-4xl mx-auto p-6 min-h-screen" style={{ background: 'linear-gradient(135deg, #232c36 0%, #3a4652 100%)' }}>
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20" style={{ background: 'rgba(35, 44, 54, 0.8)' }}>
            <div className="border-4 border-t-4 border-gray-200 border-t-blue-600 rounded-full w-12 h-12 animate-spin" />
            <p className="mt-4 text-white text-lg">Reviewing your code…</p>
          </div>
        )}

        {/* Header */}
        <header className="sticky top-0 z-10" style={{ background: 'linear-gradient(135deg, #232c36 0%, #3a4652 100%)' }}>
          <div className="flex justify-center p-4">
            <img src={logoTransparentv2} alt="Logo" width={150} />
          </div>
        </header>

        {/* Trial Status */}
        <TrialStatus />

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 cleanreview-center">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setFile(null);
                setOutput("");
              }}
              className="pb-2 font-medium transition-colors"
              style={
                isActive
                  ? { borderBottom: `2px solid ${ACCENT}`, color: ACCENT }
                  : { borderBottom: `2px solid white`, color: "white" }
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Animated Content Wrapper */}
      <div
        className={`transition-all duration-300 ease-in-out transform ${
          loaded ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
        }`}
      >
        {/* Description */}
        <p className="text-gray-600 mb-6">
          {activeTab === "full" && "Analyzes entire project."}
          {activeTab === "modified" && "Reviews modified files only."}
          {activeTab === "pr" && "GPT inline suggestions for PR."}
          {activeTab === "inline" && "Inline GPT diff annotations."}
          {activeTab === "github" && "Live GitHub PR review."}
          {/* {activeTab === "gitlab" && "GitLab MR review."} */}
        </p>

        {/* File Upload / ZIP */}
        {["full", "modified", "pr", "inline"].includes(activeTab) && (
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-200 mb-2">
              Upload ZIP File
            </label>
            <div className="relative border-2 border-dashed border-gray-300 bg-white rounded-lg h-48 flex items-center justify-center text-gray-400 hover:border-blue-400 transition-colors">
              {file ? (
                <p className="text-gray-800">{file.name}</p>
              ) : (
                <>
                  <p className="text-lg pr-2">Drop file here</p>
                  <p className="mt-1 text-sm text-gray-500">
                    or click to browse
                  </p>
                </>
              )}
              <input
                type="file"
                accept=".zip"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
          </div>
        )}

        {/* GitHub OAuth Connect */}
        {activeTab === "github" && (
          <div className="mb-6 space-y-4">
            {!githubConnected ? (
              <button
                onClick={() => {
                  // Redirect to GitHub OAuth while staying on the same tab
                  window.location.href = `${apiBaseUrl}/oauth/github/login`;
                }}
                className="block w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-center transition-colors"
              >
                🔗 Connect with GitHub
              </button>
            ) : (
              <>
                <button
                  onClick={handleDisconnect}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-center mb-2 transition-colors"
                >
                  ❌ Disconnect from GitHub
                </button>
                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2">
                    Authentication Method
                  </label>
                  <select
                    value={authMethod}
                    onChange={(e) => setAuthMethod(e.target.value)}
                    className="w-full p-3 border border-gray-600 bg-gray-800 rounded text-gray-100"
                  >
                    <option value="OAuth">OAuth</option>
                    <option value="SSH">SSH</option>
                  </select>
                </div>
                {authMethod === "SSH" && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">
                      SSH Key (optional)
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Paste your SSH key here"
                      className="w-full p-3 border border-gray-600 bg-gray-800 rounded text-gray-100"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2">
                    GitHub PR URL
                  </label>
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://github.com/owner/repo/pull/123"
                    className="w-full p-3 border border-gray-600 bg-gray-800 rounded text-gray-100 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* GitLab Inputs */}
        {activeTab === "gitlab" && (
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                GitLab MR URL
              </label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://gitlab.com/owner/repo/-/merge_requests/123"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Project ID
              </label>
              <input
                type="text"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="Project ID"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Merge Request ID
              </label>
              <input
                type="text"
                value={mrId}
                onChange={(e) => setMrId(e.target.value)}
                placeholder="Merge Request ID"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Review Output */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Review Output
          </label>
          <textarea
            readOnly
            value={output}
            rows={8}
            className="w-full p-4 border border-gray-300 bg-white rounded-lg text-gray-800 resize-none focus:outline-none"
          />
        </div>

        {/* Actions */}
        <div className="flex space-x-4">
          <button
            onClick={() => handleDownload('markdown')}
            disabled={!output}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
          >
            📄 Markdown
          </button>
          <button
            onClick={() => handleDownload('pdf')}
            disabled={!output}
            className="px-4 py-2 bg-red-500 text-white border border-red-500 rounded-lg disabled:opacity-50"
          >
            📄 PDF
          </button>
          <button
            onClick={handleSaveReview}
            disabled={!output}
            className="px-4 py-2 bg-green-500 text-white border border-green-500 rounded-lg disabled:opacity-50"
          >
            💾 Save Review
          </button>
          <button
            onClick={handleRun}
            disabled={
              loading ||
              (reviewStatus && !reviewStatus.allowed && reviewStatus.limit !== 'unlimited') ||
              (["full", "modified", "pr", "inline"].includes(activeTab) &&
                !file) ||
              (activeTab === "github" && !url) ||
              (activeTab === "gitlab" && (!url || !projectId || !mrId))
            }
            className="ml-auto px-6 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50"
          >
            {loading
              ? "Running…"
              : (reviewStatus && !reviewStatus.allowed && reviewStatus.limit !== 'unlimited')
                ? "Limit Reached"
                : `Run ${TABS.find((t) => t.key === activeTab)?.label.replace(
                    /^[^ ]+\\s*/,
                    ""
                  )}`}
          </button>
        </div>
      </div>
    </div>
    
    {/* Feedback Button */}
    <FeedbackButton />
    </div>
  );
}
